import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCheckbox,
    IonCol,
    IonIcon,
    IonLabel,
    IonRow,
    IonText,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import { Form, Formik, FormikHelpers } from 'formik';
import { informationCircleOutline } from 'ionicons/icons';
import { useAtom } from 'jotai';
import React, { memo, useCallback, useState } from 'react';
import * as Yup from 'yup';

import LoadingButton from '../../components/loadingbutton/LoadingButton';
import Signature from '../../components/signature/Signature';
import {
    accessTokenAtom,
    businessAckAtom,
    calibrationAckAtom,
    housePropertiesAtom,
    jobIdAtom,
    organisationalUnitAtom,
    photosListAtom,
    typeInstallationAtom,
    updateDBStateAtom,
} from '../../store/Atoms';
import {
    closeInterventionRequest,
    docRequest,
    uploadImageRequest,
} from '../../store/Requests';
import { ImageCategories } from '../../types/ImageInfo';
import { isCeeEnabled } from '../../utils/cee';
import { APP_NAME, ROOT_URL } from '../../utils/constants';
import { getErrorMessage } from '../../utils/errorMessage';
import { useCustomRouter } from '../../utils/hooks/useCustomRouter';
import DocumentViewerModal, {
    DocumentNames,
} from '../documentviewermodal/DocumentViewerModal';
import DefaultTextarea from '../forms/defaulttextarea/DefaultTextarea';
import AcceptanceReport from '../acceptancereport/AcceptanceReport';

type TabSignatureFormValues = {
    customerComment: string;
    calibrationAck: boolean;
    businessAck: boolean;
};

const TabSignatureSchema = (isBusiness: boolean) =>
    Yup.object().shape({
        customerComment: Yup.string(),
        calibrationAck: Yup.boolean()
            .required(t`Please acknowledge calibration`)
            .oneOf([true], t`Please acknowledge calibration`),
        businessAck: isBusiness
            ? Yup.boolean()
                  .required(t`Please acknowledge SME`)
                  .oneOf([true], t`Please acknowledge SME`)
            : Yup.boolean(),
    });

const BUSINESS_ORG_UNITS = ['TIKO_FR_PRO', 'DGP_PRO'];

const CustomerForm: React.FC = memo(() => {
    const router = useCustomRouter();
    const [presentToast] = useIonToast();
    // TODO: check this atoms
    const [photosList] = useAtom(photosListAtom);
    const [houseProperties] = useAtom(housePropertiesAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [organisationalUnit] = useAtom(organisationalUnitAtom);
    const [typeInstallation] = useAtom(typeInstallationAtom);
    const [, setBusinessAck] = useAtom(businessAckAtom);
    const [jobId] = useAtom(jobIdAtom);
    const [, updateDBState] = useAtom(updateDBStateAtom);
    const [, setCalibrationAck] = useAtom(calibrationAckAtom);
    const [honourDeclarationOpened, setHonourDeclarationOpened] =
        useState(false);
    const [customerSignatureDataUri, setCustomerSignatureDataUri] =
        useState('');
    const [installerSignatureDataUri, setInstallerSignatureDataUri] =
        useState('');
    const [showModal, setShowModal] = useState(false);

    const customerSignature = photosList.filter(
        (image: string) => !!image.match(/_SIG_/)
    )[0];
    const installerSignature = photosList.filter(
        (image: string) => !!image.match(/_ISIG_/)
    )[0];
    const hasCustomerSignature = customerSignatureDataUri || customerSignature;
    const hasInstallerSignature =
        installerSignatureDataUri || installerSignature;
    const isBusiness = BUSINESS_ORG_UNITS.includes(organisationalUnit[1]);
    const hasCee = isCeeEnabled(
        typeInstallation,
        organisationalUnit[2].hasCee
    );
    const ceeIsOlderThanTwoYears = houseProperties.ceeIsOlderThanTwoYears;
    const ceeIsClientMainResidence = houseProperties.ceeIsClientMainResidence;
    const isCeeEligible =
        hasCee && ceeIsOlderThanTwoYears && ceeIsClientMainResidence;

    const handleFormSubmit = useCallback(
        async (
            data: TabSignatureFormValues,
            actions: FormikHelpers<TabSignatureFormValues>
        ) => {
            actions.setSubmitting(true);
            try {
                const promises = [
                    docRequest(accessToken, jobId, {
                        customerComment: data.customerComment,
                    }),
                ];

                if (installerSignatureDataUri) {
                    promises.push(
                        uploadImageRequest(accessToken, jobId, {
                            typepic: ImageCategories.installerSignature,
                            typefic: 'jpg',
                            dataUri: installerSignatureDataUri as string,
                        })
                    );
                }

                if (customerSignatureDataUri) {
                    promises.push(
                        uploadImageRequest(accessToken, jobId, {
                            typepic: ImageCategories.customerSignature,
                            typefic: 'jpg',
                            dataUri: customerSignatureDataUri as string,
                        })
                    );
                }

                await Promise.all(promises);
                await closeInterventionRequest();
                await updateDBState();
                router.push('after-close', 'none');
            } catch (e: any) {
                await presentToast({
                    message: getErrorMessage(
                        e,
                        t`Oops, something went wrong while closing installation`
                    ),
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            actions.setSubmitting(false);
        },
        [
            accessToken,
            customerSignatureDataUri,
            installerSignatureDataUri,
            jobId,
            presentToast,
            router,
            updateDBState,
        ]
    );

    return (
        <Formik
            initialValues={{
                customerComment: '',
                calibrationAck: false,
                businessAck: false,
            }}
            validationSchema={TabSignatureSchema(isBusiness)}
            validateOnMount={true}
            onSubmit={handleFormSubmit}
        >
            {({
                values,
                handleChange,
                isSubmitting,
                isValid,
                setFieldValue,
            }) => (
                <Form>
                    <IonCard>
                        <IonCardContent>
                            <IonText>
                                <IonIcon
                                    icon={informationCircleOutline}
                                    size="large"
                                    className="ion-float-left ion-padding-end"
                                ></IonIcon>
                                <Trans>
                                    Please ask the customer to confirm the
                                    installation, comment if necessary and sign
                                    before closing the installation.
                                </Trans>
                            </IonText>
                        </IonCardContent>
                    </IonCard>
                    {/* <IonItem lines="none">
                                [data: Installation details <br />
                                <br />
                                Fourniture et pose de: <br /> <br />
                                x Modules de pilotage <br />
                                x Modules de communication <br />
                                x Capteurs <br />
                                <br />]
                            </IonItem> */}
                    <Signature
                        className="ion-margin-top"
                        onChange={setInstallerSignatureDataUri}
                        label={t`Installer signature`}
                        signatureImageUrl={
                            installerSignature &&
                            `${ROOT_URL}${installerSignature}?jwt=${accessToken}`
                        }
                    />
                    <DefaultTextarea
                        className="ion-margin-top"
                        name="customerComment"
                        value={values.customerComment}
                        label={t`Customer comment`}
                        onIonInput={handleChange}
                    ></DefaultTextarea>
                    <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top">
                        <IonCol>
                            <IonLabel>
                                <Trans>
                                    Please note that {APP_NAME} is a smart
                                    temperature programming solution. To
                                    maximize savings while preserving your
                                    comfort, the solution will perform two
                                    calibration cycles in the 24h following the
                                    installation. These cycles measure the
                                    thermal inertia of each room and allow the
                                    solution to heat just the right amount.
                                    Please, while a calibration cycle is
                                    ongoing, do not stop it or change the
                                    heater settings. We thank you for your
                                    understanding.
                                </Trans>
                            </IonLabel>
                        </IonCol>
                        <IonCol size="auto">
                            <IonCheckbox
                                name="calibrationAck"
                                checked={values.calibrationAck}
                                onIonChange={() => {
                                    const newValue = !values.calibrationAck;
                                    setCalibrationAck(newValue);
                                    setFieldValue('calibrationAck', newValue);
                                }}
                            />
                        </IonCol>
                    </IonRow>
                    {isBusiness && (
                        <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top">
                            <IonCol>
                                <IonLabel>
                                    <Trans>
                                        The installer has given the customer
                                        the technical file to be added to the
                                        company's safety file.
                                    </Trans>
                                </IonLabel>
                            </IonCol>
                            <IonCol size="auto">
                                <IonCheckbox
                                    name="businessAck"
                                    checked={values.businessAck}
                                    onIonChange={() => {
                                        const newValue = !values.businessAck;
                                        setBusinessAck(newValue);
                                        setFieldValue('businessAck', newValue);
                                    }}
                                />
                            </IonCol>
                        </IonRow>
                    )}

                    {isCeeEligible && (
                        <>
                            <IonButton
                                className="ion-margin-top"
                                expand="block"
                                onClick={() => setShowModal(true)}
                            >
                                <Trans>Honour declaration</Trans>
                            </IonButton>

                            <DocumentViewerModal
                                isOpen={showModal}
                                onModalClosed={() => setShowModal(!showModal)}
                                documentName={DocumentNames.HONOUR_DECLARATION}
                                onDocumentOpened={setHonourDeclarationOpened}
                            />
                        </>
                    )}
                    <AcceptanceReport/>
                    {(!isCeeEligible ||
                        (isCeeEligible && honourDeclarationOpened) ||
                        hasCustomerSignature) && (
                        <Signature
                            className="ion-margin-top"
                            onChange={setCustomerSignatureDataUri}
                            label={t`Customer signature`}
                            signatureImageUrl={
                                customerSignature &&
                                `${ROOT_URL}${customerSignature}?jwt=${accessToken}`
                            }
                        />
                    )}
                    <LoadingButton
                        className="ion-margin-top"
                        disabled={
                            !isValid ||
                            !hasCustomerSignature ||
                            !hasInstallerSignature
                        }
                        loading={isSubmitting}
                        type="submit"
                    >
                        <Trans>Close and guide the customer</Trans>
                    </LoadingButton>
                </Form>
            )}
        </Formik>
    );
});

export default CustomerForm;
